import { create } from "zustand";
import type { Evaluation } from "@/types/evaluation";
import * as db from "@/store/db";
import { generateId } from "@/utils/id";
import type { Template } from "@/types/template";
import { useTemplateStore } from "@/store/useTemplateStore";
import { parseTemplate } from "@/parser/templateParser";
import { initSubTemplateData } from "@/utils/templateResolution";
import { templateToDocument } from "@/components/unified/templateToDocument";

interface EvaluationStore {
  evaluations: Evaluation[];
  loaded: boolean;
  load: () => Promise<void>;
  create: (template: Template) => Promise<Evaluation>;
  save: (evaluation: Evaluation) => Promise<void>;
  remove: (id: string) => Promise<void>;
  getById: (id: string) => Evaluation | undefined;
}

function buildSectionToggles(
  content: ReturnType<typeof parseTemplate>["content"],
): Record<string, boolean> {
  const toggles: Record<string, boolean> = {};
  for (const node of content.content) {
    if (node.type === "section") {
      toggles[node.id] = true;
    }
  }
  return toggles;
}

function buildNewEvaluation(template: Template): Evaluation {
  const now = Date.now();
  const parsed = parseTemplate(template.source);
  const content = parsed.content;
  const dateStr = new Date(now).toLocaleDateString();
  const getParsedContent = useTemplateStore.getState().getParsedContent;

  return {
    createdAt: now,
    evalType: "unified",
    fieldToggles: {},
    fillVariables: {},
    id: generateId(),
    name: `${content.name} — ${dateStr}`,
    sectionToggles: buildSectionToggles(content),
    status: "in-progress",
    subTemplateValues: initSubTemplateData(content.content, getParsedContent),
    templateId: template.id,
    templateName: content.name,
    unifiedDocument: templateToDocument(content) as Record<string, unknown>,
    updatedAt: now,
    values: {},
    variables: [...content.variables],
  };
}

export const useEvaluationStore = create<EvaluationStore>((set, get) => ({
  create: async (template: Template) => {
    const evaluation = buildNewEvaluation(template);
    await db.saveEvaluation(evaluation);
    set((s) => ({ evaluations: [evaluation, ...s.evaluations] }));
    return evaluation;
  },
  evaluations: [],

  getById: (id: string) => get().evaluations.find((e) => e.id === id),

  load: async () => {
    const evaluations = await db.getAllEvaluations();
    evaluations.sort((a, b) => b.updatedAt - a.updatedAt);
    set({ evaluations, loaded: true });
  },

  loaded: false,

  remove: async (id: string) => {
    await db.deleteEvaluation(id);
    set((s) => ({ evaluations: s.evaluations.filter((e) => e.id !== id) }));
  },

  save: async (evaluation: Evaluation) => {
    const updated = { ...evaluation, updatedAt: Date.now() };
    await db.saveEvaluation(updated);
    set((s) => ({
      evaluations: s.evaluations
        .map((e) => (e.id === updated.id ? updated : e))
        .toSorted((a, b) => b.updatedAt - a.updatedAt),
    }));
  },
}));
